import type { Declaration, SchemaFieldType } from "./types.ts";

export const INTRINSIC_MODULE = "_intrinsic";

const b = (name: string): SchemaFieldType => ({ category: "builtin", name });
const bf = (count: number): SchemaFieldType => ({ category: "bitfield", count });
type IntrinsicDef = {
  name: string;
  fields?: { name: string; offset: number; type: SchemaFieldType }[];
  size: number;
};

const types: IntrinsicDef[] = [
  {
    name: "Color",
    fields: [
      { name: "r", offset: 0, type: b("uint8") },
      { name: "g", offset: 1, type: b("uint8") },
      { name: "b", offset: 2, type: b("uint8") },
      { name: "a", offset: 3, type: b("uint8") },
    ],
    size: 4,
  },
  {
    name: "DegreeEuler",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
    ],
    size: 12,
  },
  {
    name: "fltx4",
    fields: [
      {
        name: "m128_f32",
        offset: 0,
        type: { category: "fixed_array", inner: b("float32"), count: 4 },
      },
    ],
    size: 16,
  },
  {
    name: "FourVectors",
    fields: [
      { name: "x", offset: 0, type: { category: "atomic", name: "fltx4" } },
      { name: "y", offset: 16, type: { category: "atomic", name: "fltx4" } },
      { name: "z", offset: 32, type: { category: "atomic", name: "fltx4" } },
    ],
    size: 48,
  },
  {
    name: "matrix3x4_t",
    fields: [
      {
        name: "m_flMatVal",
        offset: 0,
        type: { category: "fixed_array", inner: b("float32"), count: 12 },
      },
    ],
    size: 48,
  },
  {
    name: "matrix3x4a_t",
    fields: [
      {
        name: "m_flMatVal",
        offset: 0,
        type: { category: "fixed_array", inner: b("float32"), count: 12 },
      },
    ],
    size: 48,
  },
  {
    name: "QAngle",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
    ],
    size: 12,
  },
  {
    name: "Quaternion",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
      { name: "w", offset: 12, type: b("float32") },
    ],
    size: 16,
  },
  {
    name: "QuaternionAligned",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
      { name: "w", offset: 12, type: b("float32") },
    ],
    size: 16,
  },
  {
    name: "RadianEuler",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
    ],
    size: 12,
  },
  {
    name: "Range_t",
    fields: [
      { name: "m_flMin", offset: 0, type: b("float32") },
      { name: "m_flMax", offset: 4, type: b("float32") },
    ],
    size: 8,
  },
  {
    name: "Vector",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
    ],
    size: 12,
  },
  {
    name: "Vector2D",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
    ],
    size: 8,
  },
  {
    name: "Vector4D",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
      { name: "w", offset: 12, type: b("float32") },
    ],
    size: 16,
  },
  {
    name: "VectorAligned",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
      { name: "w", offset: 12, type: b("float32") },
    ],
    size: 16,
  },
  {
    name: "VectorWS",
    fields: [
      { name: "x", offset: 0, type: b("float32") },
      { name: "y", offset: 4, type: b("float32") },
      { name: "z", offset: 8, type: b("float32") },
    ],
    size: 12,
  },
  {
    name: "CTransform",
    fields: [
      { name: "m_vPosition", offset: 0, type: { category: "atomic", name: "VectorAligned" } },
      {
        name: "m_orientation",
        offset: 16,
        type: { category: "atomic", name: "QuaternionAligned" },
      },
    ],
    size: 32,
  },
  {
    name: "CTransformWS",
    fields: [
      { name: "m_vPosition", offset: 0, type: { category: "atomic", name: "VectorWS" } },
      { name: "m_orientation", offset: 16, type: { category: "atomic", name: "Quaternion" } },
    ],
    size: 32,
  },

  {
    name: "CEntityHandle",
    fields: [
      { name: "m_EntityIndex", offset: 0, type: bf(15) },
      { name: "m_Serial", offset: 0, type: bf(17) },
    ],
    size: 4,
  },
  {
    name: "CEntityIndex",
    fields: [{ name: "m_Data", offset: 0, type: b("int32") }],
    size: 4,
  },
  {
    name: "CHandle",
    fields: [
      { name: "m_EntityIndex", offset: 0, type: bf(15) },
      { name: "m_Serial", offset: 0, type: bf(17) },
    ],
    size: 4,
  },
  {
    name: "CPlayerSlot",
    fields: [{ name: "m_Data", offset: 0, type: b("int32") }],
    size: 4,
  },
  {
    name: "CSplitScreenSlot",
    fields: [{ name: "m_Data", offset: 0, type: b("int32") }],
    size: 4,
  },

  {
    name: "CBufferString",
    fields: [
      { name: "m_nLength", offset: 0, type: b("int32") },
      { name: "m_nAllocatedSize", offset: 4, type: b("int32") },
      {
        name: "m_szString",
        offset: 8,
        type: { category: "fixed_array", inner: b("char"), count: 8 },
      },
    ],
    size: 16,
  },
  {
    name: "CUtlString",
    fields: [{ name: "m_pString", offset: 0, type: { category: "ptr", inner: b("char") } }],
    size: 8,
  },
  {
    name: "CUtlSymbol",
    fields: [{ name: "m_Id", offset: 0, type: b("uint16") }],
    size: 2,
  },
  {
    name: "CUtlSymbolLarge",
    fields: [{ name: "m_pString", offset: 0, type: { category: "ptr", inner: b("char") } }],
    size: 8,
  },
  {
    name: "CUtlStringToken",
    fields: [{ name: "m_nHashCode", offset: 0, type: b("uint32") }],
    size: 4,
  },
  {
    // Interned string. _MakeGlobalSymbol returns the pointer
    name: "CGlobalSymbol",
    fields: [{ name: "m_pString", offset: 0, type: { category: "ptr", inner: b("char") } }],
    size: 8,
  },
  {
    name: "CGlobalSymbolCaseSensitive",
    fields: [{ name: "m_pString", offset: 0, type: { category: "ptr", inner: b("char") } }],
    size: 8,
  },
  {
    // m_pszString points into the inline storage
    name: "CKV3MemberNameWithStorage",
    fields: [
      { name: "m_nHashCode", offset: 0, type: b("uint32") },
      { name: "m_SymId", offset: 4, type: b("int32") },
      { name: "m_pszString", offset: 8, type: { category: "ptr", inner: b("char") } },
      { name: "m_nLength", offset: 16, type: b("int32") },
      { name: "m_nAllocatedSize", offset: 20, type: b("int32") },
      {
        name: "m_szString",
        offset: 24,
        type: { category: "fixed_array", inner: b("char"), count: 32 },
      },
    ],
    size: 56,
  },
  {
    name: "CUtlStringTokenNoRegistration",
    fields: [{ name: "m_nHashCode", offset: 0, type: b("uint32") }],
    size: 4,
  },
  {
    name: "CSoundEventName",
    fields: [
      { name: "m_nLength", offset: 0, type: b("int32") },
      { name: "m_nAllocatedSize", offset: 4, type: b("int32") },
      {
        name: "m_szString",
        offset: 8,
        type: { category: "fixed_array", inner: b("char"), count: 8 },
      },
    ],
    size: 16,
  },
  {
    // CBufferString with 200 bytes inline, followed by two unknown 8 byte fields
    name: "CResourceName",
    fields: [
      { name: "m_nLength", offset: 0, type: b("int32") },
      { name: "m_nAllocatedSize", offset: 4, type: b("int32") },
      {
        name: "m_szString",
        offset: 8,
        type: { category: "fixed_array", inner: b("char"), count: 200 },
      },
    ],
    size: 224,
  },
  {
    name: "CResourceNameTyped",
    fields: [
      { name: "m_nLength", offset: 0, type: b("int32") },
      { name: "m_nAllocatedSize", offset: 4, type: b("int32") },
      {
        name: "m_szString",
        offset: 8,
        type: { category: "fixed_array", inner: b("char"), count: 200 },
      },
    ],
    size: 224,
  },
  {
    name: "WorldGroupId_t",
    fields: [{ name: "m_nHashCode", offset: 0, type: b("uint32") }],
    size: 4,
  },

  {
    name: "CUtlVector",
    fields: [
      { name: "m_Size", offset: 0, type: b("int32") },
      { name: "m_pMemory", offset: 8, type: { category: "ptr", inner: b("void") } },
      { name: "m_nAllocationCount", offset: 16, type: b("int32") },
      { name: "m_nGrowSize", offset: 20, type: b("int32") },
    ],
    size: 24,
  },
  {
    name: "CNetworkUtlVectorBase",
    fields: [
      { name: "m_Size", offset: 0, type: b("int32") },
      { name: "m_pMemory", offset: 8, type: { category: "ptr", inner: b("void") } },
      { name: "m_nAllocationCount", offset: 16, type: b("int32") },
      { name: "m_nGrowSize", offset: 20, type: b("int32") },
    ],
    size: 24,
  },
  {
    name: "C_NetworkUtlVectorBase",
    fields: [
      { name: "m_Size", offset: 0, type: b("int32") },
      { name: "m_pMemory", offset: 8, type: { category: "ptr", inner: b("void") } },
      { name: "m_nAllocationCount", offset: 16, type: b("int32") },
      { name: "m_nGrowSize", offset: 20, type: b("int32") },
    ],
    size: 24,
  },
  {
    // Followed by N inline elements. The size depends on them
    name: "CUtlVectorFixedGrowable",
    fields: [
      { name: "m_Size", offset: 0, type: b("int32") },
      { name: "m_pMemory", offset: 8, type: { category: "ptr", inner: b("void") } },
      { name: "m_nAllocationCount", offset: 16, type: b("int32") },
      { name: "m_nGrowSize", offset: 20, type: b("int32") },
    ],
    size: 24,
  },
  {
    name: "CUtlLeanVector",
    fields: [
      { name: "m_nCount", offset: 0, type: b("int32") },
      { name: "m_nAllocated", offset: 4, type: b("int32") },
      { name: "m_pElements", offset: 8, type: { category: "ptr", inner: b("void") } },
    ],
    size: 16,
  },
  {
    // Elements are inline at offset 8 until it grows. The size depends on them
    name: "CUtlLeanVectorFixedGrowable",
    fields: [
      { name: "m_nCount", offset: 0, type: b("int32") },
      { name: "m_nAllocated", offset: 4, type: b("int32") },
      { name: "m_pElements", offset: 8, type: { category: "ptr", inner: b("void") } },
    ],
    size: 16,
  },
  {
    name: "CUtlHashtable",
    fields: [
      { name: "m_table", offset: 0, type: { category: "atomic", name: "CUtlLeanVector" } },
      { name: "m_nUsed", offset: 16, type: b("int32") },
      { name: "m_nTableSize", offset: 20, type: b("int32") },
      { name: "m_nMinSize", offset: 24, type: b("int32") },
      { name: "m_bSizeLocked", offset: 28, type: b("bool") },
    ],
    size: 32,
  },
  {
    // Red-black tree
    name: "CUtlOrderedMap",
    fields: [
      { name: "m_LessFunc", offset: 0, type: { category: "ptr", inner: b("void") } },
      { name: "m_Elements", offset: 8, type: { category: "atomic", name: "CUtlLeanVector" } },
      { name: "m_Root", offset: 24, type: b("int32") },
      { name: "m_NumElements", offset: 28, type: b("int32") },
      { name: "m_FirstFree", offset: 32, type: b("int32") },
      { name: "m_LastAlloc", offset: 36, type: b("int32") },
    ],
    size: 40,
  },
  {
    // CUtlMap of names
    name: "CUtlDict",
    fields: [
      { name: "m_LessFunc", offset: 0, type: { category: "ptr", inner: b("void") } },
      { name: "m_Elements", offset: 8, type: { category: "atomic", name: "CUtlLeanVector" } },
      { name: "m_Root", offset: 24, type: b("int32") },
      { name: "m_NumElements", offset: 28, type: b("int32") },
      { name: "m_FirstFree", offset: 32, type: b("int32") },
      { name: "m_LastAlloc", offset: 36, type: b("int32") },
    ],
    size: 40,
  },
  {
    name: "CUtlBinaryBlock",
    fields: [
      { name: "m_nActualLength", offset: 0, type: b("int32") },
      { name: "m_nAllocationCount", offset: 4, type: b("int32") },
      { name: "m_pMemory", offset: 8, type: { category: "ptr", inner: b("uint8") } },
    ],
    size: 16,
  },

  {
    // Other aliases: CKV3MemberNameSet
    name: "KeyValues3",
    fields: [
      { name: "m_bContextIndependent", offset: 0, type: bf(1) },
      { name: "m_bFreeArrayMemory", offset: 0, type: bf(1) },
      { name: "m_TypeEx", offset: 0, type: bf(8) },
      { name: "m_SubType", offset: 0, type: bf(8) },
      { name: "m_nFlags", offset: 0, type: bf(8) },
      { name: "m_nClusterElement", offset: 0, type: bf(16) },
      { name: "m_nNumArrayElements", offset: 0, type: bf(5) },
      { name: "m_Data", offset: 8, type: b("uint64") },
    ],
    size: 16,
  },
  {
    name: "CKeyValues3Array",
    fields: [
      { name: "m_nClusterElement", offset: 0, type: b("int32") },
      { name: "m_nAllocatedChunks", offset: 4, type: b("int32") },
      { name: "m_nCount", offset: 8, type: b("int32") },
      { name: "m_nInitialSize", offset: 12, type: b("uint8") },
      { name: "m_bIsDynamicallySized", offset: 13, type: b("bool") },
      { name: "m_pDynamicElements", offset: 16, type: { category: "ptr", inner: b("void") } },
    ],
    size: 64,
  },
  {
    name: "CKeyValues3Table",
    fields: [
      { name: "m_nClusterElement", offset: 0, type: b("int32") },
      { name: "m_nAllocatedChunks", offset: 4, type: b("int32") },
      { name: "m_pFastSearch", offset: 8, type: { category: "ptr", inner: b("void") } },
      { name: "m_nCount", offset: 16, type: b("int32") },
      { name: "m_nInitialSize", offset: 20, type: b("uint8") },
      { name: "m_bIsDynamicallySized", offset: 21, type: b("bool") },
      { name: "m_pDynamicBuffer", offset: 24, type: { category: "ptr", inner: b("void") } },
    ],
    size: 192,
  },

  {
    name: "CResourceArray",
    fields: [
      { name: "m_nOffset", offset: 0, type: b("int32") },
      { name: "m_nCount", offset: 4, type: b("uint32") },
    ],
    size: 8,
  },
  {
    name: "CResourcePointer",
    fields: [{ name: "m_nOffset", offset: 0, type: b("int32") }],
    size: 4,
  },
  {
    name: "CResourceString",
    fields: [{ name: "m_nOffset", offset: 0, type: b("int32") }],
    size: 4,
  },
  {
    name: "CSmartPtr",
    fields: [{ name: "m_pObj", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },
  {
    name: "CStrongHandle",
    fields: [{ name: "m_pBinding", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },
  {
    name: "CStrongHandleCopyable",
    fields: [{ name: "m_pBinding", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },
  {
    name: "CStrongHandleVoid",
    fields: [{ name: "m_pBinding", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },
  {
    name: "CWeakHandle",
    fields: [{ name: "m_pBinding", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },

  {
    name: "CNetworkedQuantizedFloat",
    fields: [
      { name: "m_Value", offset: 0, type: b("float32") },
      { name: "m_nEncoder", offset: 4, type: b("uint16") },
      { name: "m_bUnflattened", offset: 6, type: b("bool") },
    ],
    size: 8,
  },
  {
    name: "CVariantBase",
    fields: [
      { name: "m_int32", offset: 0, type: b("int32") },
      { name: "m_uint32", offset: 0, type: b("uint32") },
      { name: "m_int64", offset: 0, type: b("int64") },
      { name: "m_uint64", offset: 0, type: b("uint64") },
      { name: "m_float32", offset: 0, type: b("float32") },
      { name: "m_float64", offset: 0, type: b("float64") },
      { name: "m_pszString", offset: 0, type: { category: "ptr", inner: b("char") } },
      { name: "m_pVector", offset: 0, type: { category: "ptr", inner: b("void") } },
      { name: "m_pData", offset: 0, type: { category: "ptr", inner: b("void") } },
      { name: "m_char", offset: 0, type: b("char") },
      { name: "m_bool", offset: 0, type: b("bool") },
      { name: "m_hScript", offset: 0, type: { category: "atomic", name: "HSCRIPT" } },
      { name: "m_hEntity", offset: 0, type: { category: "atomic", name: "CEntityHandle" } },
      {
        name: "m_utlStringToken",
        offset: 0,
        type: { category: "atomic", name: "CUtlStringToken" },
      },
      { name: "m_type", offset: 8, type: b("int16") },
      { name: "m_flags", offset: 10, type: b("uint16") },
    ],
    size: 16,
  },

  {
    name: "SndOpEventGuid_t",
    fields: [
      { name: "m_nGuid", offset: 0, type: b("uint32") },
      { name: "m_hStackHash", offset: 4, type: b("uint32") },
      { name: "m_nFlags", offset: 8, type: b("int32") },
      { name: "m_nRecipients", offset: 12, type: b("uint64") },
    ],
    size: 20,
  },
  {
    // Polymorphic. The vtable is CEmbeddedSubclass<T>'s own
    name: "CEmbeddedSubclass",
    fields: [
      { name: "__vftable", offset: 0, type: { category: "ptr", inner: b("void") } },
      { name: "m_pData", offset: 8, type: { category: "ptr", inner: b("void") } },
    ],
    size: 16,
  },
  {
    name: "HSCRIPT",
    fields: [{ name: "m_pScript", offset: 0, type: { category: "ptr", inner: b("void") } }],
    size: 8,
  },
  {
    name: "V_uuid_t",
    fields: [
      { name: "Data1", offset: 0, type: b("uint32") },
      { name: "Data2", offset: 4, type: b("uint16") },
      { name: "Data3", offset: 6, type: b("uint16") },
      { name: "Data4", offset: 8, type: { category: "fixed_array", inner: b("uint8"), count: 8 } },
    ],
    size: 16,
  },
];

export const intrinsicDeclarations = new Map<string, Declaration>(
  types.map((c): [string, Declaration] => [
    c.name,
    {
      kind: "class",
      name: c.name,
      module: INTRINSIC_MODULE,
      size: c.size,
      flags: [],
      parents: [],
      fields: (c.fields ?? []).map((f) => ({ ...f, metadata: [] })),
      metadata: [],
    },
  ]),
);
